import { useRSO } from "@/api/services/admin/rso";
import { createRSOUserApi } from "@/api/services/admin/rso/user";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { createPartnerUserSchema } from "@/features/rso/schema/partnerUserSchema";
import { TCreatePartnerUserForm } from "@/features/rso/types/createPartnerUserForm";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { orange } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { forwardRef, useImperativeHandle } from "react";
import React from "react";
import { IconWarning } from "@/assets/icons/IconWarning";
import { isEmpty } from "@/utils/object";

const initCreateForm: Partial<TCreatePartnerUserForm> = {
  fullName: "",
  email: "",
};

type TCreatePartnerUserFormProps = {};

export interface CreatePartnerUserFormRef {
  submit: () => void;
  isFormDirty: () => boolean;
}

export const CreatePartnerUserForm = forwardRef<CreatePartnerUserFormRef, TCreatePartnerUserFormProps>((props, ref) => {
  const toast = useToast();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: rsoResult } = useRSO();
  const rsoName = rsoResult?.data?.name;
  const methods = useForm<TCreatePartnerUserForm>({
    defaultValues: initCreateForm,
    resolver: yupResolver(createPartnerUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, watch, trigger, formState } = methods;
  const isUser = watch("isUser");

  // Trigger email validation when isUser changes
  React.useEffect(() => {
    if (isUser !== undefined && isUser !== null) {
      trigger("email");
    }
  }, [isUser, trigger]);

  const onSubmit = async (data: TCreatePartnerUserForm) => {
    try {
      if (!id) throw new Error();
      const payload = { ...data, isUser: Boolean(data.isUser) };
      const rs = await createRSOUserApi(id, payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }));
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_USER_LIST] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  // Check if form should block navigation
  const isFormDirty = () => {
    // If form is submitted and has no errors, don't block
    if (formState.isSubmitted && isEmpty(formState.errors)) {
      return false;
    }
    // If there are no dirty fields, don't block
    if (isEmpty(formState.dirtyFields)) {
      return false;
    }
    // Otherwise, block if form has changes
    return true;
  };

  // Expose submit and isFormDirty for parent
  useImperativeHandle(ref, () => ({
    submit: async () => {
      await methods.handleSubmit(onSubmit)();
    },
    isFormDirty,
  }));

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={createPartnerUserSchema}>
      <Grid container columnSpacing={3} rowSpacing={2}>
        <Grid item xs={6}>
          <FormField label="顧客名">
            <Input value={rsoName || ""} disabled />
          </FormField>
        </Grid>
        <Grid item xs={6} />
        <Grid item xs={6}>
          <FormField control={control} name="fullName">
            <Input placeholder="氏名" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="email">
            <Input placeholder="メールアドレス" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="isUser">
            <Select
              placeholder="顧客ユーザー / irohana担当者"
              options={[
                { label: "irohana担当者", value: 0 },
                { label: "顧客ユーザー", value: 1 },
              ]}
            />
            <Stack direction="row" alignContent="center" mt={1}>
              <IconWarning />
              <Typography variant="cap12" color={orange[80]}>
                当社がログインするためのユーザーは「irohana担当者」に変更すること
              </Typography>
            </Stack>
          </FormField>
        </Grid>
      </Grid>
    </Form>
  );
});
